import React, { useCallback, useEffect, useRef, useState } from 'react';
import '@brands/Dashboard/MultiStage/multiStage.scss';
import {
  CaseIcon,
  caseTypeLabel,
  isConvertibleCaseType,
  statusLabel,
  statusVariant,
} from '@brands/Dashboard/MultiStage/multiStageUi';
import {
  canRoleAddCase,
  canRoleCancelCase,
  canRoleCancelJourney,
  canRoleCompleteCase,
  canRoleManageJourney,
} from '@brands/Utils/getCollapsibleHeader';
import { tokens } from '@brands/Dashboard/MultiStage/multiStageTokens';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment-timezone';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { toast } from 'react-toastify';

import {
  CaseJourneysResponse,
  CaseStatusAction,
  JourneyCase,
  JourneyGroup,
  JourneyStatusAction,
  convertCaseToJourney,
  createLinkedCase,
  getCaseJourneys,
  getFollowUpOptions,
  updateCaseStatus,
  updateJourneyStatus,
} from '@brands/services/cases/multiStage';

const MAX_JOURNEY_CASES = 7;

// Case types that occupy a booked slot, so their date line reads as an
// appointment ("Scheduled …" / "Not yet scheduled") rather than a creation
// date. video_call_instant stays here: it's still a live call type elsewhere
// in this module (TypeIcon and CLINICAL_CASE_TYPES in multiStageUi.tsx group
// it with video_call_scheduled), just retired from new intake. care_coordination
// was removed: unlike the others, it never gets a video_conference_date (it's
// async, backed by a CareCoordinationReport instead), which made this branch
// read it as permanently unscheduled. The backend's SYNC_CASE_TYPES /
// ASYNC_CASE_TYPES lists (routers/cases/shared.py,
// lib/notification/create_case_updated_notification.py) agree it's async.
const SYNC_TYPES = new Set<string>([
  'video_call_scheduled',
  'phone_call_scheduled',
  'oral_health_coaching',
  'video_call_instant',
]);
const COMPLETE = new Set<string>(['completed', 'completed_by_provider']);

// DO-4518: the backend only assigns the raw `upcoming` status to a linked
// case the patient still has to act on (finish intake and/or supply
// payment) before staff can pick it up — narrower than the "Upcoming" pill
// label, which also covers ordinary not-yet-scheduled cases (see
// statusVariant/statusLabel in multiStageUi.tsx).
const needsPatientAction = (c: JourneyCase): boolean => c.status === 'upcoming';

// Small circular "!" badge shared by the pending-action line and the
// program-level banner below, matching MyCareJourney.tsx's action-needed banner.
const WarningBadge = ({ size = 22, fontSize = 13 }: { size?: number; fontSize?: number }): JSX.Element => (
  <span
    style={{
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: '50%',
      background: tokens.danger,
      color: '#fff',
      display: 'grid',
      placeItems: 'center',
      fontWeight: 700,
      fontSize,
    }}
  >
    !
  </span>
);

const fmtDate = (v?: string | null): string =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
// The API sends appointment times as naive UTC (no trailing Z, no offset), which
// `new Date()` would read as *local* time and shift by the viewer's offset. Parse
// as UTC explicitly, then convert to the viewer's zone and label it, so the time
// shown is the one they are expected to attend.
const fmtDateTime = (v?: string | null): string =>
  v ? moment.utc(v).tz(moment.tz.guess()).format('MMM D, YYYY · h:mmA z') : '';

// Date semantics: completed -> completed date; sync -> scheduled date (or "Not
// yet scheduled"); async -> creation date.
const caseDateLabel = (c: JourneyCase): string => {
  // A finished case must never fall through to the scheduling branch below: the
  // `completed_by_provider` path never stamps `completed_at`, so gating this on a
  // date would label a case a provider already closed as "Not yet scheduled".
  if (COMPLETE.has(c.status)) {
    return c.completed_at ? `Completed ${fmtDate(c.completed_at)}` : 'Completed';
  }
  if (SYNC_TYPES.has(c.type)) {
    return c.video_conference_date ? `Scheduled ${fmtDateTime(c.video_conference_date)}` : 'Not yet scheduled';
  }
  return `Created ${fmtDate(c.created_at)}`;
};

// A div[role="button"] must suppress Space's default page scroll itself; native buttons do this internally.
const onActivateKey =
  (activate: () => void) =>
  (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };

interface MenuOption {
  label: string;
  value: string;
  danger?: boolean;
}

// Close a popover when the user clicks outside of it.
const useOutside = (ref: React.RefObject<HTMLElement>, onClose: () => void): void => {
  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
};

// A status pill that opens a dropdown of status actions. When disabled it
// renders as a plain (non-interactive) pill.
const StatusPill = ({
  status,
  options,
  disabled,
  onSelect,
}: {
  status: string;
  options: MenuOption[];
  disabled?: boolean;
  onSelect: (value: string) => void;
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutside(ref, () => setOpen(false));
  const interactive = !disabled && options.length > 0;
  return (
    <div className="ms-menu-wrap" ref={ref} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`ms-pill ms-pill-btn ${statusVariant(status)}`}
        disabled={!interactive}
        onClick={() => interactive && setOpen((o) => !o)}
      >
        {statusLabel(status)}
        {interactive && <span className="ms-caret" />}
      </button>
      {open && (
        <div className="ms-menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={opt.danger ? 'is-danger' : ''}
              onClick={() => {
                setOpen(false);
                onSelect(opt.value);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Starting a case is clinical work owned by whoever picks the case up, so no
// role forces one into progress from here: the only status actions are closing
// it out (complete) and canceling it.
const caseStatusOptions = (canCancel = true, canComplete = true): MenuOption[] =>
  [
    canComplete && { label: 'Mark complete', value: 'completed' },
    canCancel && { label: 'Cancel case', value: 'canceled', danger: true },
  ].filter((opt): opt is MenuOption => Boolean(opt));

const programStatusOptions = (status: string, canManage: boolean, canCancel: boolean): MenuOption[] => {
  if (status !== 'in_progress' && status !== 'paused') {
    // complete / canceled
    return [];
  }
  const cancel = canCancel ? [{ label: 'Cancel program', value: 'canceled', danger: true }] : [];
  if (!canManage) {
    return cancel;
  }
  const toggle =
    status === 'in_progress'
      ? { label: 'Pause program', value: 'paused' }
      : { label: 'Resume program', value: 'in_progress' };
  return [toggle, ...cancel];
};

// Module-scope so identity is stable across parent re-renders (a re-render
// otherwise remounts these and drops the standalone row's open menu state).
const CaseMeta = ({ c }: { c: JourneyCase }): JSX.Element => (
  <span className="ms-case-meta">
    <div className="ms-case-eyebrow">Case #{c.id}</div>
    <div className="ms-case-title">{caseTypeLabel(c.type)}</div>
    <div className="ms-case-sub">{caseDateLabel(c)}</div>
    {needsPatientAction(c) && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        <WarningBadge size={16} fontSize={10} />
        <span style={{ color: tokens.warning, fontSize: 13, fontWeight: 600 }}>Pending patient action</span>
      </div>
    )}
  </span>
);

const StandaloneRow = ({
  c,
  busy,
  canCancel,
  canComplete,
  canManageJourney,
  onCaseStatus,
  onConvert,
  onOpen,
}: {
  c: JourneyCase;
  busy: boolean;
  canCancel: boolean;
  canComplete: boolean;
  canManageJourney: boolean;
  onCaseStatus: (caseId: number, status: string) => void;
  onConvert: (c: JourneyCase) => void;
  onOpen: (caseId: number) => void;
}): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  return (
    <div
      className={`ms-case-card${needsPatientAction(c) ? ' is-action-needed' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(c.id)}
      onKeyDown={onActivateKey(() => onOpen(c.id))}
      style={{ marginBottom: 12, cursor: 'pointer' }}
    >
      <CaseIcon status={c.status} />
      <CaseMeta c={c} />
      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <StatusPill
          status={c.status}
          options={c.status === 'canceled' || COMPLETE.has(c.status) ? [] : caseStatusOptions(canCancel, canComplete)}
          disabled={busy}
          onSelect={(v) => onCaseStatus(c.id, v)}
        />
        {canManageJourney && isConvertibleCaseType(c.type) && (
          // React events bubble through the Menu's portal, so stop them before they reach the row.
          <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <IconButton size="small" disabled={busy} onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem
                disabled={c.status === 'canceled'}
                onClick={() => {
                  setAnchorEl(null);
                  onConvert(c);
                }}
              >
                Convert to multi-stage case
              </MenuItem>
            </Menu>
          </span>
        )}
      </span>
    </div>
  );
};

interface ToastInput {
  kind: 'success' | 'info' | 'error';
  title: string;
  msg: string;
}

interface ConfirmState {
  title: string;
  body: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
}

// Staff view of a patient's cases, split into multi-stage programs (journeys)
// and standalone cases, with per-case and per-program status actions, manual
// add-case, and convert-to-program.
const ProfileCasesSection = ({ patientId }: { patientId: number }): JSX.Element | null => {
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);
  const canManageJourney = canRoleManageJourney(userInfo?.role.name || '');
  const canCancel = canRoleCancelCase(userInfo?.role.name || '');
  const canCancelJourney = canRoleCancelJourney(userInfo?.role.name || '');
  const canComplete = canRoleCompleteCase(userInfo?.role.name || '');
  const canAddCase = canManageJourney && canRoleAddCase(userInfo?.role.name || '');
  const [data, setData] = useState<CaseJourneysResponse | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [addForJourney, setAddForJourney] = useState<number | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [inlineErr, setInlineErr] = useState<Record<number, string>>({});
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Uses the app-wide toast stack (bottom center of the content area) rather
  // than a list of its own, which drew a differently styled stack at the top
  // right.
  const pushToast = useCallback(({ kind, title, msg }: ToastInput): void => {
    toast(`${title}. ${msg}`, { type: kind });
  }, []);

  const load = useCallback(async (): Promise<CaseJourneysResponse | null> => {
    if (!patientId) {
      return null;
    }
    try {
      const res = await getCaseJourneys(patientId);
      setData(res);
      return res;
    } catch {
      return null;
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const pushProgramStatusToast = (status: JourneyStatusAction): void => {
    if (status === 'paused') {
      pushToast({ kind: 'info', title: 'Program paused', msg: 'All cases in this program are paused.' });
    } else if (status === 'canceled') {
      pushToast({ kind: 'success', title: 'Program canceled', msg: 'All open cases in this program were canceled.' });
    } else {
      pushToast({ kind: 'success', title: 'Program updated', msg: 'Program is now in progress.' });
    }
  };

  // ---- Program status (pause / resume / cancel) ----
  const runProgramStatus = async (journeyId: number, status: JourneyStatusAction): Promise<void> => {
    setBusy(true);
    try {
      await updateJourneyStatus(journeyId, status);
      await load();
      pushProgramStatusToast(status);
    } catch {
      // The mutation may have actually succeeded server-side despite the
      // error response (e.g. a retried request hitting an already-updated
      // record) — refetch and check whether it landed before deciding which
      // toast to show, so staff aren't told to retry an update that already
      // went through (which would then hit a real conflict on retry).
      const refreshed = await load();
      const journey = refreshed?.multi_stage.find((j) => j.case_journey_id === journeyId);
      if (journey?.status === status) {
        pushProgramStatusToast(status);
      } else {
        pushToast({ kind: 'error', title: "Couldn't update program", msg: 'Please try again in a moment.' });
      }
    } finally {
      setBusy(false);
    }
  };

  const onProgramStatus = (journey: JourneyGroup, status: string): void => {
    if (status === 'canceled') {
      setConfirm({
        title: 'Cancel this program?',
        body: "All open cases inside this program will be canceled and the patient will be notified. Completed cases remain on the patient's record. This can't be undone.",
        confirmLabel: 'Cancel program',
        danger: true,
        onConfirm: () => {
          setConfirm(null);
          runProgramStatus(journey.case_journey_id, 'canceled');
        },
      });
      return;
    }
    runProgramStatus(journey.case_journey_id, status as JourneyStatusAction);
  };

  const pushCaseStatusToast = (status: CaseStatusAction): void => {
    if (status === 'canceled') {
      pushToast({
        kind: 'success',
        title: 'Case canceled',
        msg: 'The case has been canceled and the patient notified.',
      });
    } else {
      pushToast({ kind: 'success', title: 'Case updated', msg: `Case is now ${statusLabel(status).toLowerCase()}.` });
    }
  };

  const findCaseStatus = (res: CaseJourneysResponse | null, caseId: number): string | undefined =>
    res?.standalone.find((c) => c.id === caseId)?.status ??
    res?.multi_stage.flatMap((j) => j.cases).find((c) => c.id === caseId)?.status;

  // ---- Case status (mark complete / in progress / cancel) ----
  const runCaseStatus = async (caseId: number, status: CaseStatusAction): Promise<void> => {
    setBusy(true);
    try {
      await updateCaseStatus(caseId, status);
      await load();
      pushCaseStatusToast(status);
    } catch {
      // See runProgramStatus above: refetch and check whether the update
      // actually landed before choosing the toast, instead of unconditionally
      // telling staff to retry an update that may have already succeeded.
      const refreshed = await load();
      if (findCaseStatus(refreshed, caseId) === status) {
        pushCaseStatusToast(status);
      } else {
        pushToast({ kind: 'error', title: "Couldn't update case", msg: 'Please try again in a moment.' });
      }
    } finally {
      setBusy(false);
    }
  };

  const onCaseStatus = (caseId: number, status: string): void => {
    if (status === 'canceled') {
      setConfirm({
        title: 'Cancel this case?',
        body: "The case will be marked canceled and the patient will be notified. This can't be undone.",
        confirmLabel: 'Cancel case',
        danger: true,
        onConfirm: () => {
          setConfirm(null);
          runCaseStatus(caseId, 'canceled');
        },
      });
      return;
    }
    runCaseStatus(caseId, status as CaseStatusAction);
  };

  // ---- Convert standalone -> program ----
  const onConvert = (c: JourneyCase): void => {
    setConfirm({
      title: 'Convert to multi-stage case?',
      body: `This will move "${caseTypeLabel(
        c.type,
      )}" into a new multi-stage program. You can then add additional cases (up to ${MAX_JOURNEY_CASES}) to plan the patient's care.`,
      confirmLabel: 'Convert',
      onConfirm: async () => {
        setConfirm(null);
        setBusy(true);
        try {
          await convertCaseToJourney(c.id);
          await load();
          pushToast({
            kind: 'success',
            title: 'Converted to multi-stage',
            msg: 'The case is now part of a new multi-stage program.',
          });
        } catch {
          pushToast({ kind: 'error', title: "Couldn't convert case", msg: 'Please try again in a moment.' });
        } finally {
          setBusy(false);
        }
      },
    });
  };

  // ---- Add case to a program ----
  const openAdd = async (journey: JourneyGroup): Promise<void> => {
    if (journey.cases.length >= MAX_JOURNEY_CASES) {
      setInlineErr((e) => ({
        ...e,
        [journey.case_journey_id]:
          "You've reached the 7-case maximum for this program. Cancel or complete a case before adding another.",
      }));
      pushToast({ kind: 'error', title: "Can't add an 8th case", msg: 'This program has reached the 7-case maximum.' });
      return;
    }
    const anchor = journey.cases[journey.cases.length - 1];
    if (!anchor) {
      return;
    }
    try {
      const res = await getFollowUpOptions(anchor.id);
      setOptions(res.allowed_case_types);
      setSelectedType(res.allowed_case_types[0] || '');
      setAddForJourney(journey.case_journey_id);
    } catch {
      /* ignore */
    }
  };

  const submitAdd = async (journey: JourneyGroup): Promise<void> => {
    const anchor = journey.cases[journey.cases.length - 1];
    if (!anchor || !selectedType) {
      return;
    }
    setBusy(true);
    try {
      await createLinkedCase(anchor.id, { case_type: selectedType, assign_to_me: false });
      setAddForJourney(null);
      await load();
      pushToast({
        kind: 'success',
        title: 'Case added',
        msg: `A ${caseTypeLabel(selectedType)} case was added to the program.`,
      });
    } finally {
      setBusy(false);
    }
  };

  const dismissInlineErr = (journeyId: number): void =>
    setInlineErr((e) => {
      const next = { ...e };
      delete next[journeyId];
      return next;
    });

  if (!data) {
    return null;
  }

  const openCase = (caseId: number): void => {
    // The profile switches tabs via history.replaceState, which React Router doesn't observe, so the
    // path App.tsx tracks lacks the active tab's hash. Save it here so Back returns to the Cases tab.
    sessionStorage.setItem(
      'lastNonConsultationPagePath',
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
    roleNavigate(`/consultation-page/${caseId}`, { state: { returnViaHistory: true } });
  };

  const renderProgramCase = (c: JourneyCase, programStatus: string): JSX.Element => {
    const locked = programStatus !== 'in_progress' && programStatus !== 'paused';
    return (
      <div
        key={c.id}
        className={`ms-case-card${needsPatientAction(c) ? ' is-action-needed' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => openCase(c.id)}
        onKeyDown={onActivateKey(() => openCase(c.id))}
        style={{ cursor: 'pointer' }}
      >
        <CaseIcon status={c.status} />
        <CaseMeta c={c} />
        <StatusPill
          status={c.status}
          options={c.status === 'canceled' || COMPLETE.has(c.status) ? [] : caseStatusOptions(canCancel, canComplete)}
          disabled={busy || locked}
          onSelect={(v) => onCaseStatus(c.id, v)}
        />
      </div>
    );
  };

  return (
    <div className="ms-scope" style={{ marginTop: 24 }}>
      <h3 className="ms-title" style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
        Multi-stage cases
      </h3>
      {data.multi_stage.length === 0 ? (
        <div className="ms-empty-card">
          <div className="ms-empty-title">No multi-stage cases yet</div>
          Convert a standalone case to start a multi-stage program.
        </div>
      ) : (
        data.multi_stage.map((journey) => {
          const canAdd =
            canAddCase &&
            journey.cases.length < MAX_JOURNEY_CASES &&
            (journey.status === 'in_progress' || journey.status === 'complete');
          return (
            <div key={journey.case_journey_id} className="ms-card" style={{ marginBottom: 16 }}>
              {journey.cases.some(needsPatientAction) && (
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    background: tokens.warningBg,
                    border: `1px solid ${tokens.warning}`,
                    borderRadius: tokens.radiusMd,
                    padding: '16px 20px',
                    marginBottom: 14,
                  }}
                >
                  <WarningBadge />
                  <p style={{ margin: 0, color: tokens.navy, fontWeight: 600, fontSize: 14 }}>
                    Waiting on the patient to complete intake before this case can be scheduled.
                  </p>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                {/* 70% product navy, per the master design system. */}
                <span style={{ color: 'rgba(13, 32, 53, 0.7)', fontSize: 15 }}>
                  Started {fmtDate(journey.started_at)}
                </span>
                <StatusPill
                  status={journey.status}
                  options={programStatusOptions(journey.status, canManageJourney, canCancelJourney)}
                  disabled={busy}
                  onSelect={(v) => onProgramStatus(journey, v)}
                />
              </div>
              {journey.cases.map((c) => renderProgramCase(c, journey.status))}
              {addForJourney === journey.case_journey_id ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center', justifyContent: 'center' }}>
                  <select
                    className="ms-field-select"
                    style={{ width: 'auto' }}
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    {options.map((o) => (
                      <option key={o} value={o}>
                        {caseTypeLabel(o)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ms-add-case-btn"
                    disabled={busy || !selectedType}
                    onClick={() => submitAdd(journey)}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    className="ms-cancel-link"
                    style={{ width: 'auto', margin: 0, padding: '0 8px' }}
                    onClick={() => setAddForJourney(null)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                canManageJourney && (
                  <div className="ms-add-case-row">
                    <button
                      type="button"
                      className="ms-add-case-btn"
                      disabled={busy || !canAdd}
                      title={
                        !canAddCase
                          ? 'Only a Super Admin, Client Admin, Collector, or Coordinator can add cases'
                          : !canAdd && journey.cases.length < MAX_JOURNEY_CASES
                          ? 'Resume the program to add cases'
                          : ''
                      }
                      onClick={() => openAdd(journey)}
                    >
                      + Add case
                    </button>
                  </div>
                )
              )}
              {inlineErr[journey.case_journey_id] && (
                <div className="ms-inline-err">
                  {inlineErr[journey.case_journey_id]}
                  <button type="button" onClick={() => dismissInlineErr(journey.case_journey_id)}>
                    ×
                  </button>
                </div>
              )}
              <p className="ms-card-footnote">
                Include 3&ndash;5 cases per program for optimal outcomes. Maximum of 7.
              </p>
            </div>
          );
        })
      )}

      <h3 className="ms-title" style={{ fontSize: 20, fontWeight: 700, margin: '24px 0 12px' }}>
        Standalone cases
      </h3>
      {data.standalone.length === 0 ? (
        <div className="ms-empty-card">
          <div className="ms-empty-title">No standalone cases</div>
        </div>
      ) : (
        data.standalone.map((c) => (
          <StandaloneRow
            key={c.id}
            c={c}
            busy={busy}
            canCancel={canCancel}
            canComplete={canComplete}
            canManageJourney={canManageJourney}
            onCaseStatus={onCaseStatus}
            onConvert={onConvert}
            onOpen={openCase}
          />
        ))
      )}

      {confirm && (
        <div className="ms-modal-backdrop" onClick={() => setConfirm(null)}>
          <div className="ms-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>{confirm.title}</h3>
            <p>{confirm.body}</p>
            <div className="ms-confirm-actions">
              <button type="button" className="ms-btn-ghost" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={confirm.danger ? 'ms-btn-danger' : 'ms-primary-btn'}
                style={confirm.danger ? undefined : { width: 'auto' }}
                onClick={confirm.onConfirm}
              >
                {confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCasesSection;
